import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

interface TimelineDataPoint {
  date: string;
  verified: number;
  flagged: number;
  users?: number;
}

interface RegistrationsTimelineChartProps {
  data: TimelineDataPoint[];
  showUserSignups?: boolean;
}

const chartConfig = {
  verified: {
    label: "Verified Registrations",
    color: "hsl(var(--chart-1))",
  },
  flagged: {
    label: "Needs Review",
    color: "hsl(45 93% 47%)", // amber
  },
  users: {
    label: "User Signups",
    color: "hsl(var(--chart-2))",
  },
};

const RegistrationsTimelineChart: React.FC<RegistrationsTimelineChartProps> = ({
  data,
  showUserSignups = true,
}) => {
  // Check if there's any data to display
  const hasData = data.some(d => d.verified > 0 || d.flagged > 0 || (d.users && d.users > 0));

  if (!hasData) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Registrations Over Time</h3>
        <div className="h-[300px] flex items-center justify-center text-muted-foreground">
          No registration data available yet
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-semibold text-foreground mb-4">Registrations & Users Over Time</h3>
      <ChartContainer config={chartConfig} className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
              allowDecimals={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Legend
              verticalAlign="top"
              height={36}
              formatter={(value) => chartConfig[value as keyof typeof chartConfig]?.label || value}
            />
            <Line
              type="monotone"
              dataKey="verified"
              stroke="hsl(var(--chart-1))"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="flagged"
              stroke="hsl(45 93% 47%)"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
            {showUserSignups && (
              <Line
                type="monotone"
                dataKey="users"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};

export default RegistrationsTimelineChart;
